import { useState, useEffect, useCallback } from "react";
import { RvDto, rvFormData } from "../types/rv";
import { rvService } from "../services/rvService";
import { useApi } from "./useApi";
import { useMutation } from "./useMutation";

export const useRvs = () => {
  const {
    data: rvs = [],
    loading: isFetching,
    error: getError,
    refetch: fetchRvs,
    invalidateCache,
  } = useApi<RvDto[]>("rvs", (signal) => rvService.getAll({}, signal));

  // Ξεχωριστά states για τον μήνα και την ημέρα
  const [monthRvs, setMonthRvs] = useState<RvDto[]>([]);
  const [isMonthLoading, setIsMonthLoading] = useState<boolean>(false);
  const [monthError, setMonthError] = useState<any>(null);

  const [dayRvs, setDayRvs] = useState<RvDto[]>([]);
  const [isDayLoading, setIsDayLoading] = useState<boolean>(false);
  const [dayError, setDayError] = useState<any>(null);

  const fetchByDay = useCallback(async (date: string) => {
    setIsDayLoading(true);
    setDayError(null);
    try {
      const data = await rvService.getByDay(date);
      setDayRvs(data);
    } catch (err: any) {
      setDayError(err);
      setDayRvs([]);
    } finally {
      setIsDayLoading(false);
    }
  }, []); // <-- Κενό array για να είναι σταθερή η συνάρτηση

  const fetchByMonth = useCallback(async (year: number, month: number) => {
    setIsMonthLoading(true);
    setMonthError(null);
    try {
      const data = await rvService.getByMonth(year, month);
      setMonthRvs(data);
    } catch (err: any) {
      setMonthError(err);
      setMonthRvs([]);
    } finally {
      setIsMonthLoading(false);
    }
  }, []); // <-- Κενό array για να είναι σταθερή η συνάρτηση

  const [newPatientsRvs, setNewPatientsRvs] = useState<RvDto[]>([]);
  const [isNewPatientsLoading, setIsNewPatientsLoading] = useState<boolean>(false);
  const [newPatientsError, setNewPatientsError] = useState<any>(null);

  const fetchNewPatients = useCallback(async () => {
    setIsNewPatientsLoading(true);
    setNewPatientsError(null);
    try {
      // Υποθέτουμε ότι στο rvService θα προσθέσεις μια μέθοδο getNewPatients()
      const data = await rvService.getNewPatients(); 
      setNewPatientsRvs(data);
    } catch (err: any) {
      setNewPatientsError(err);
      setNewPatientsRvs([]);
    } finally {
      setIsNewPatientsLoading(false);
    }
  }, []);
  const {
    execute: createExecution,
    loading: isCreating,
    error: createError,
  } = useMutation((newRv: Partial<RvDto>) => rvService.create(newRv as RvDto));

  const {
    execute: updateExecution,
    loading: isUpdating,
    error: updateError,
  } = useMutation(({ id, data }: { id: number | string; data: RvDto }) =>
    rvService.update(id, data)
  );

  const {
    execute: deleteExecution,
    loading: isDeleting,
    error: deleteError,
  } = useMutation(async (ids: number[]) => {
    await Promise.all(ids.map((id) => rvService.delete(id)));
    return ids;
  });

  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error";
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "success",
  });

  const closeModal = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  };

  const createRv = async (newRv: typeof rvFormData): Promise<void> => {
    try {
      await createExecution(newRv);
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Το ραντεβού αποθηκεύτηκε επιτυχώς!",
        type: "success",
      });
      fetchRvs(true);
    } catch (err: any) {
      const errorMsg = 
        err.response?.data?.message || 
        err.response?.data || 
        err.message || 
        "Σφάλμα δημιουργίας ραντεβού";

      setModalState({
        isOpen: true,
        title: "Σφάλμα Επικάλυψης",
        message: typeof errorMsg === 'string' ? errorMsg : "Σφάλμα δημιουργίας ραντεβού",
        type: "error",
      });
    }
  };

  const updateRv = async (id: number | string, updatedData: RvDto): Promise<void> => {
    try {
      await updateExecution({ id, data: updatedData });
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η ενημέρωση του ραντεβού ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      fetchRvs(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα ενημέρωσης ραντεβού";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  const deleteRvs = async (ids: number[]): Promise<void> => {
    try {
      await deleteExecution(ids);
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η διαγραφή των ραντεβού ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      fetchRvs(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα κατά τη διαγραφή ραντεβού";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  return {
    rvs,
    monthRvs,
    isMonthLoading,
    dayRvs,
    isDayLoading,
    newPatientsRvs,
    isNewPatientsLoading,
    isLoading: isFetching || isCreating || isUpdating || isDeleting,
    error: getError || createError || updateError || deleteError || monthError || dayError,
    fetchRvs: () => fetchRvs(true),
    fetchByDay,
    fetchByMonth,
    fetchNewPatients,
    createRv,
    updateRv,
    deleteRvs,
    modalState,
    closeModal,
  };
};